'use client'

import React from 'react'

import { XMarkIcon } from '@heroicons/react/24/outline'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { WithRequired } from '@tanstack/react-query'
import { capitalize, compact } from 'lodash'
import { Drawer } from 'vaul'

import { Button } from '~/components/component-library/Button'
import { type ButtonProps } from '~/components/component-library/Button/types'
import { useDialogKeyboard } from '~/components/component-library/Dialog/useDialogKeyboard'
import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'
import { useIsMobile } from '~/utils/devices'
import { cn } from '~/utils/tailwind-utils'
import { sendTrackingData } from '~/utils/tracking'

const InsideDialogContext = React.createContext(false)
export const useIsInsideDialog = () => React.use(InsideDialogContext)

const Dialog: React.FC<
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>
> = ({ ...props }) => {
  const isMobile = useIsMobile()

  // NOTE: Radix dialog is forcing pointer events to none on body after closing
  // https://github.com/radix-ui/primitives/issues/1241#issuecomment-2462326355
  React.useEffect(() => {
    return () => {
      setTimeout(() => {
        if (typeof document !== 'undefined') {
          document.body.style.pointerEvents = ''
        }
      }, 50)
    }
  }, [])

  if (isMobile) {
    return (
      <Drawer.Root
        {...props}
        repositionInputs={false}
        shouldScaleBackground={false}
        modal={true}
      />
    )
  }
  return <DialogPrimitive.Root {...props} />
}
Dialog.displayName = DialogPrimitive.Root.displayName

const DialogTrigger = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger> & {
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<typeof DialogPrimitive.Trigger> | null>
}) => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <Drawer.Trigger
        ref={ref}
        className={cn('cursor-pointer outline-none', className)}
        {...props}
      />
    )
  } else {
    return (
      <DialogPrimitive.Trigger
        ref={ref}
        className={cn('cursor-pointer outline-none', className)}
        {...props}
      />
    )
  }
}
DialogTrigger.displayName = DialogPrimitive.Trigger.displayName

const DialogPortal = DialogPrimitive.Portal

const DialogClose = ({
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close> & {
  ref?: React.RefObject<React.ElementRef<typeof DialogPrimitive.Close> | null>
}) => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return <Drawer.Close ref={ref} {...props} />
  } else {
    return <DialogPrimitive.Close ref={ref} {...props} />
  }
}
DialogClose.displayName = DialogPrimitive.Close.displayName

const DialogOverlay = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<typeof DialogPrimitive.Overlay> | null>
}) => {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 backdrop-brightness-[85%] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 dark:backdrop-brightness-[10%]',
        className,
      )}
      {...props}
    />
  )
}
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContentBody = ({
  ref,
  id,
  children,
  closeButton,
  dialogTitle,
  hideTitle,
  clampTitle,
  isLoading,
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  className?: string
  id?: string
  closeButton: ICloseButtonProps
  dialogTitle: React.ReactNode
  hideTitle: boolean
  clampTitle?: boolean
  isLoading?: boolean
} & {
  ref?: React.RefObject<React.ElementRef<typeof DialogPrimitive.Content> | null>
}) => {
  const isMobile = useIsMobile()

  const onCloseClick = () => {
    sendTrackingData('closeDialog', { id })
  }

  const closeButtonElement = closeButton.enabled && !isMobile && (
    <DialogPrimitive.Close
      className="ml-4 rounded-lg border border-gray-200 p-0.5 text-secondary ring-offset-white hover:bg-gray-100 disabled:pointer-events-none dark:border-gray-700 dark:bg-gray-800 dark:ring-offset-gray-950 dark:hover:bg-gray-900 dark:focus:ring-gray-500"
      onClick={onCloseClick}
    >
      <XMarkIcon className="h-5 w-5" strokeWidth={2} />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  )

  return (
    <InsideDialogContext value={true}>
      {closeButton.enabled && isMobile && (
        <div className="absolute left-0 top-0 w-full">
          <Drawer.Handle className="my-2 !w-12 !bg-gray-500" />
        </div>
      )}

      <div
        ref={ref}
        className={cn('flex w-full flex-col rounded-2xl', {
          'max-h-[90vh] overflow-hidden': !isMobile,
          'min-h-0 overflow-y-auto': isMobile,
          'pointer-events-none select-none': isLoading,
        })}
      >
        {dialogTitle &&
          (hideTitle ? (
            <VisuallyHidden>
              <DialogTitle
                className={cn({
                  'line-clamp-2': clampTitle !== false,
                })}
              >
                {dialogTitle}
              </DialogTitle>
            </VisuallyHidden>
          ) : (
            <DialogHeader
              className={cn('mb-4 px-6 md:pt-6', {
                'opacity-25': isLoading,
              })}
            >
              <DialogTitle
                className={cn('grow', {
                  'line-clamp-2': clampTitle !== false,
                })}
              >
                {dialogTitle}
              </DialogTitle>
              {closeButtonElement}
            </DialogHeader>
          ))}

        {children}
      </div>
    </InsideDialogContext>
  )
}
DialogContentBody.displayName = 'DialogContentBody'

const DialogContent = ({
  ref,
  id,
  className,
  children,
  closeButton,
  dialogTitle,
  hideTitle,
  clampTitle,
  isLoading,
  maxWidth = 'md',
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  className?: string
  id?: string
  closeButton: ICloseButtonProps
  dialogTitle: React.ReactNode
  hideTitle: boolean
  clampTitle?: boolean
  isLoading?: boolean
  maxWidth?: 'md' | 'xl'
} & {
  ref?: React.RefObject<React.ElementRef<typeof DialogPrimitive.Content> | null>
}) => {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          className,
          'modal',
          'z-50 w-full outline-none focus:outline-none focus-visible:outline-none md:fixed md:left-[50%] md:top-[50%] md:grid md:translate-x-[-50%] md:translate-y-[-50%]',
          {
            'max-w-md': maxWidth === 'md',
            'max-w-xl': maxWidth === 'xl',
          },
        )}
        aria-describedby={undefined}
        data-dialog-id={id}
        tabIndex={-1} // Make sure not in tab order, but can receive focus
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          ;(event.currentTarget as HTMLElement).focus()
        }}
        {...props}
      >
        <DialogContentBody
          id={id}
          closeButton={closeButton}
          dialogTitle={dialogTitle}
          hideTitle={hideTitle}
          clampTitle={clampTitle}
          isLoading={isLoading}
        >
          {children}
        </DialogContentBody>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}
DialogContent.displayName = DialogPrimitive.Content.displayName

const DrawerContent = ({
  ref,
  id,
  className,
  children,
  closeButton,
  dialogTitle,
  hideTitle,
  clampTitle,
  isLoading,
  zIndex = 50,
  ...props
}: React.ComponentPropsWithoutRef<typeof Drawer.Content> & {
  className?: string
  id?: string
  closeButton: ICloseButtonProps
  dialogTitle: React.ReactNode
  hideTitle: boolean
  clampTitle?: boolean
  isLoading?: boolean
  zIndex?: number
} & {
  ref?: React.RefObject<React.ElementRef<typeof Drawer.Content> | null>
}) => {
  return (
    <Drawer.Portal>
      <Drawer.Overlay
        className="fixed inset-0 bg-black/40"
        style={{ zIndex }}
      />
      <Drawer.Content
        ref={ref}
        className={cn(
          'border border-gray-200 bg-white text-primary dark:border-gray-800 dark:bg-gray-900',
          'fixed bottom-0 left-0 right-0 mt-24 flex flex-col rounded-t-3xl py-6',
          'max-h-[calc(100vh-6rem)] overflow-hidden',
          '[--vaul-animation-duration:200ms]',
          className,
        )}
        style={{ zIndex }}
        aria-describedby={undefined}
        data-dialog-id={id}
        onOpenAutoFocus={(event) => event.preventDefault()}
        {...props}
      >
        <DialogContentBody
          id={id}
          className={className}
          closeButton={closeButton}
          dialogTitle={dialogTitle}
          hideTitle={hideTitle}
          clampTitle={clampTitle}
          isLoading={isLoading}
        >
          {children}
        </DialogContentBody>
      </Drawer.Content>
    </Drawer.Portal>
  )
}
DrawerContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  className?: string
}) => {
  return (
    <div
      className={cn(
        'flex flex-row items-start justify-between text-left',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

DialogHeader.displayName = 'DialogHeader'

const DialogTitle = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<typeof DialogPrimitive.Title> | null>
}) => {
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <Drawer.Title
        ref={ref}
        className={cn(
          'w-full text-ellipsis text-wrap break-words text-base font-medium',
          className,
        )}
        {...props}
      />
    )
  } else {
    return (
      <DialogPrimitive.Title
        ref={ref}
        className={cn(
          'w-full text-ellipsis text-wrap break-words text-lg font-medium',
          className,
        )}
        {...props}
        // Adds an html native tooltip so it's possible to see the full title if it's too long
        title={typeof props.children === 'string' ? props.children : undefined}
      />
    )
  }
}
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof DialogPrimitive.Description
  > | null>
}) => (
  <div ref={ref} className={cn('text-left text-sm', className)} {...props} />
)
DialogDescription.displayName = DialogPrimitive.Description.displayName

const DialogBody = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  className?: string
} & {
  ref?: React.RefObject<React.ElementRef<
    typeof DialogPrimitive.Description
  > | null>
}) => <div ref={ref} className={cn('text-base', className)} {...props} />
DialogBody.displayName = 'DialogBody'

interface IDialogContentButtonProps extends Omit<ButtonProps, 'isLoading'> {
  text?: string
}

type IDeleteButtonProps = IDialogContentButtonProps

type IActionButtonProps = IDialogContentButtonProps

type ICancelButtonProps = IDialogContentButtonProps

type ICloseButtonProps = {
  enabled: boolean
}

type DialogContentProps = React.ComponentProps<typeof DialogPrimitive.Content>

interface ISplitsDialogContentProps {
  id: string
  title: React.ReactNode
  hideTitle: boolean
  children?: React.ReactNode[] | React.ReactNode | string
  onClose?: () => void
  deleteButton?: IDeleteButtonProps
  actionButton?:
    IActionButtonProps | { element: React.ReactElement<{ fullWidth: boolean }> }
  cancelButton?: ICancelButtonProps
  closeButton?: ICloseButtonProps
  clampTitle?: boolean
  onPointerDownOutside?: DialogContentProps['onPointerDownOutside']
  isLoading?: boolean
  maxWidth?: 'md' | 'xl'
}

function renderButton(buttonProps: IDialogContentButtonProps) {
  const { onClick, text, variant, size, shortcut, ...restProps } = buttonProps

  // Handle the discriminated union properly
  // Exclude shortcut for secondaryAction variant
  const buttonPropsToPass =
    variant === 'secondaryAction'
      ? { ...restProps, variant: 'secondaryAction' as const }
      : {
          ...restProps,
          variant: variant || ('primary' as const),
          size,
          shortcut,
        }

  if (onClick) {
    return (
      <Button {...buttonPropsToPass} onClick={onClick}>
        {text}
      </Button>
    )
  }
  return (
    <DialogClose asChild>
      <Button {...buttonPropsToPass}>{text}</Button>
    </DialogClose>
  )
}

const SplitsDialogContentActions = ({
  deleteButton,
  actionButton,
  cancelButton,
  isLoading,
}: {
  deleteButton?: IDeleteButtonProps
  actionButton?:
    IActionButtonProps | { element: React.ReactElement<{ fullWidth: boolean }> }
  cancelButton?: ICancelButtonProps
  isLoading?: boolean
}) => {
  const isMobile = useIsMobile()
  const numberOfButtons = compact([
    actionButton,
    cancelButton,
    deleteButton,
  ]).length

  const fullWidth = isMobile && numberOfButtons === 1

  const buttons = (
    <div
      className={cn('z-10', {
        'flex justify-end space-x-4': !isMobile,
        'grid gap-4': isMobile,
        'grid-cols-1': isMobile && numberOfButtons === 1,
        'grid-cols-2': isMobile && numberOfButtons === 2,
      })}
    >
      {cancelButton &&
        renderButton({
          ...cancelButton,
          fullWidth,
          text: cancelButton.text || 'Cancel',
          variant: 'secondary',
          isDisabled: cancelButton.isDisabled || isLoading,
        })}
      {actionButton &&
        ('element' in actionButton
          ? React.cloneElement(actionButton.element, {
              ...actionButton.element.props,
              fullWidth,
              shortcut: { key: '⌘+↵' },
            } as React.ComponentProps<typeof Button>)
          : renderButton({
              ...actionButton,
              fullWidth,
              isDisabled: actionButton.isDisabled || isLoading,
              shortcut: { key: '⌘+↵' },
            }))}
      {deleteButton &&
        renderButton({
          ...deleteButton,
          fullWidth,
          variant: 'danger',
          isDisabled: deleteButton.isDisabled || isLoading,
          shortcut: { key: '⌘+↵' },
        })}
    </div>
  )

  return <div className="flex flex-col space-y-3 px-6 pb-6 pt-4">{buttons}</div>
}

const SplitsBaseDialogContent = ({
  id,
  title,
  children,
  actionButton,
  deleteButton,
  cancelButton,
  closeButton,
  hideTitle,
  clampTitle,
  onPointerDownOutside,
  isLoading,
  maxWidth = 'md',
}: ISplitsDialogContentProps) => {
  const isMobile = useIsMobile()

  const hasButtons = deleteButton || actionButton || cancelButton

  // Handle keyboard shortcuts for dialog actions
  useDialogKeyboard({
    id,
    isLoading,
    actionButton,
    deleteButton,
  })

  const body = (
    <DialogBody
      className={cn({
        'flex min-h-0 flex-col': !isMobile,
      })}
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          className={cn(
            'w-full flex-1 space-y-4 overflow-y-auto break-words px-6 py-0.5',
            {
              'max-h-[60vh]': isMobile,
              'opacity-25': isLoading,
              'pb-6': !hasButtons && !isMobile,
              'pt-6': hideTitle && !isMobile,
            },
          )}
        >
          {children}
        </div>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center text-secondary">
            <LoadingSpinner size="3xl" />
          </div>
        )}
      </div>
      {hasButtons && (
        <div
          className={cn('z-10 bg-white dark:bg-black', {
            'dark:bg-gray-900': isMobile,
            'opacity-25': isLoading,
          })}
        >
          <SplitsDialogContentActions
            deleteButton={deleteButton}
            actionButton={actionButton}
            cancelButton={cancelButton}
            isLoading={isLoading}
          />
        </div>
      )}
    </DialogBody>
  )

  if (isMobile) {
    return (
      <DrawerContent
        id={id}
        closeButton={closeButton ?? { enabled: true }}
        dialogTitle={title}
        hideTitle={hideTitle}
        clampTitle={clampTitle}
        isLoading={isLoading}
      >
        {body}
      </DrawerContent>
    )
  }
  return (
    <DialogContent
      id={id}
      closeButton={closeButton ?? { enabled: true }}
      dialogTitle={title}
      hideTitle={hideTitle}
      clampTitle={clampTitle}
      isLoading={isLoading}
      aria-describedby={undefined}
      onPointerDownOutside={onPointerDownOutside}
      maxWidth={maxWidth}
    >
      {body}
    </DialogContent>
  )
}

type VariantSpecificSplitsDialogContentProps =
  | {
      variant: 'cta'
      actionButton:
        | WithRequired<IActionButtonProps, 'text' | 'onClick'>
        | { element: React.ReactElement }
      cancelButton?: ICancelButtonProps
    }
  | {
      variant: 'confirmation'
      confirmButton: WithRequired<IActionButtonProps, 'text' | 'onClick'>
      cancelButton?: ICancelButtonProps
    }
  | {
      variant: 'destructive'
      deleteButton: WithRequired<IDeleteButtonProps, 'text' | 'onClick'>
      cancelButton?: ICancelButtonProps
    }
  | { variant: 'info' }

type SplitsDialogContentProps = {
  id: string
  title: React.ReactNode
  children: React.ReactNode
  hideTitle?: boolean
  clampTitle?: boolean
  onPointerDownOutside?: ISplitsDialogContentProps['onPointerDownOutside']
  isLoading?: boolean
  maxWidth?: 'md' | 'xl'
} & VariantSpecificSplitsDialogContentProps

const SplitsDialogContent = (props: SplitsDialogContentProps) => {
  const dialogProps: Omit<
    React.ComponentProps<typeof SplitsBaseDialogContent>,
    'children'
  > = {
    id: props.id,
    title: props.title,
    hideTitle: props.hideTitle ?? false,
    clampTitle: props.clampTitle ?? true,
    onPointerDownOutside: props?.onPointerDownOutside,
    isLoading: props.isLoading,
    maxWidth: props.maxWidth,
  }

  const cancelEventName = `cancel${capitalize(props.id)}Dialog`
  const submitEventName = `submit${capitalize(props.id)}Dialog`

  if (props.variant === 'cta') {
    dialogProps.actionButton = {
      eventName: submitEventName,
      ...props.actionButton,
    }
    if (props.cancelButton) {
      dialogProps.cancelButton = {
        eventName: cancelEventName,
        ...props.cancelButton,
      }
    }
  }

  if (props.variant === 'confirmation') {
    dialogProps.actionButton = {
      eventName: submitEventName,
      ...props.confirmButton,
    }
    dialogProps.cancelButton = {
      eventName: cancelEventName,
      ...(props.cancelButton ?? {}),
    }
    dialogProps.closeButton = { enabled: false }
  }

  if (props.variant === 'destructive') {
    dialogProps.deleteButton = {
      eventName: submitEventName,
      ...props.deleteButton,
    }
    dialogProps.cancelButton = {
      eventName: cancelEventName,
      ...(props.cancelButton ?? {}),
    }
    dialogProps.closeButton = { enabled: false }
  }

  return (
    <SplitsBaseDialogContent {...dialogProps}>
      {props.children}
    </SplitsBaseDialogContent>
  )
}

export { Dialog, DialogTrigger, DrawerContent, SplitsDialogContent }
export type { IDialogContentButtonProps }
